import { useEffect, useState } from 'react';

/**
 * Hook to detect and sync system theme preference with HTML element
 * Listens to changes in prefers-color-scheme and updates the 'dark' class on HTML element
 */
export const useTheme = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check initial theme preference
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    // Set initial theme class
    if (typeof window !== 'undefined') {
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }

    // Listen for theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e?: MediaQueryListEvent) => {
      const matches = e ? e.matches : mediaQuery.matches;
      setIsDarkMode(matches);
      
      // Update HTML element class immediately
      if (matches) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };

    // Add listener for changes
    // Use addEventListener if available (modern browsers)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      
      // Cleanup
      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    } else {
      // Fallback for older browsers (deprecated but supported)
      const listener = handleChange as (this: MediaQueryList, ev?: MediaQueryListEvent) => void;
      mediaQuery.addListener(listener);
      
      // Cleanup
      return () => {
        mediaQuery.removeListener(listener);
      };
    }
  }, []);

  return isDarkMode;
};

